import React, { useRef, useState } from 'react';
import axios from 'axios';
import { X, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectForm = ({ formData, setFormData, onSubmit, onCancel, isEditing, error }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: data.url }));
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="project-modal">
      <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-primary" data-testid="modal-title">
            {isEditing ? 'Edit Project' : 'Add New Project'}
          </h2>
          <button onClick={onCancel} className="text-text_muted hover:text-primary" data-testid="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700" data-testid="modal-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Project Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Client Name *</label>
              <input type="text" name="client_name" value={formData.client_name} onChange={handleChange} required className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-client-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Location *</label>
                <input type="text" name="location" value={formData.location} onChange={handleChange} required className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-location" />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Capacity *</label>
                <input type="text" name="capacity" value={formData.capacity} onChange={handleChange} required placeholder="e.g., 5 MW" className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-capacity" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Status *</label>
                <select name="status" value={formData.status} onChange={handleChange} required className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-status">
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Completion Date</label>
                <input type="text" name="completion_date" value={formData.completion_date} onChange={handleChange} placeholder="e.g., December 2024" className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-completion-date" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Project Image</label>
              <div className="space-y-3">
                {formData.image_url && (
                  <div className="relative w-full h-40 rounded overflow-hidden border border-border">
                    <img src={getImageSrc(formData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full" data-testid="remove-image">
                      <X size={14} />
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-text_muted mb-1 font-medium">Choose from your device:</label>
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="w-full text-sm text-text_muted file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-opacity-90 file:cursor-pointer cursor-pointer border border-input rounded" data-testid="file-input" />
                  {uploading && <p className="text-xs text-accent mt-1 font-medium">Uploading...</p>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-text_muted">OR</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>
                <input type="text" name="image_url" value={formData.image_url} onChange={handleChange} placeholder="Paste image URL here..." className="w-full px-4 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm" data-testid="input-image-url" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Description *</label>
              <textarea name="description" value={formData.description} onChange={handleChange} required rows="4" className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" data-testid="input-description"></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Video URL (YouTube or MP4)</label>
              <input type="url" name="video_url" value={formData.video_url} onChange={handleChange} placeholder="https://www.youtube.com/watch?v=... or https://...mp4" className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-video-url" />
              <p className="text-xs text-text_muted mt-1">Paste a YouTube link or direct video URL to embed in the project card</p>
            </div>

            <div className="flex space-x-4">
              <button type="submit" className="flex-1 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all" data-testid="modal-submit">
                {isEditing ? 'Update Project' : 'Create Project'}
              </button>
              <button type="button" onClick={onCancel} className="px-6 py-3 bg-surface text-text_main rounded font-semibold hover:bg-border transition-all" data-testid="modal-cancel">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
